import { cloneElement, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Podpowiedź renderowana przez portal do <body>.
//
// Poprzednia wersja umieszczała dymek jako `absolute` wewnątrz przycisku, a te
// przyciski siedzą w kontenerze tabeli z `overflow-x-auto`. Element pozycjonowany
// absolutnie jest przycinany przez przodka z overflow, więc podpowiedzi przy
// akcjach w tabeli były ucinane albo w ogóle niewidoczne. Portal wyprowadza
// dymek poza to drzewo, a pozycję liczymy z getBoundingClientRect().
//
// Obsługiwany jest też focus, nie tylko hover — inaczej podpowiedź nie istnieje
// dla osoby poruszającej się po interfejsie klawiaturą.

export function Tooltip({ label, children, side = 'top' }) {
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);

  const show = useCallback(() => {
    const element = triggerRef.current;
    if (!element || !label) return;

    const rect = element.getBoundingClientRect();
    setPosition({
      left: rect.left + rect.width / 2,
      top: side === 'bottom' ? rect.bottom + 8 : rect.top - 8,
    });
  }, [label, side]);

  const hide = useCallback(() => setPosition(null), []);

  const trigger = cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
  });

  // whitespace-pre-line w klasach dymka: część podpowiedzi (np. rozbicie
  // kosztu składowania) jest wielolinijkowa i rozdzielona znakami \n.
  return (
    <>
      {trigger}
      {position &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              left: position.left,
              top: position.top,
              transform: side === 'bottom' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            }}
            className="pointer-events-none z-[100] max-w-xs animate-fade-in whitespace-pre-line rounded-md border border-line bg-content px-2 py-1 text-2xs font-medium leading-relaxed text-app shadow-pop"
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
}

export default Tooltip;
